import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  ColDef,
  IDatasource,
  IGetRowsParams,
} from "ag-grid-community";
import { TableHeaderToolTipComponent } from "src/app/shared/table-header-tool-tip/table-header-tool-tip.component";
import { ServerRequest } from "src/app/_models/global.data.model";
import { AssetService } from "src/app/_services/asset.service";
import { DynamicFormService } from "src/app/_services/dynamic-form.service";
import { ErrorUtil } from "src/app/_utilities/error";
import { SubscriptionUtil } from "src/app/_utilities/subscription";
import { DynamicTableColumnsService } from "src/app/_services/dynamic-table-column.service";
import { Global } from "src/app/_utilities/global";
import { LanguageTranslateService } from "src/app/_services/language-translate.service";
import moment from "moment";

@Component({
  selector: "app-custella-contracts-table",
  templateUrl: "./custella-contracts-table.component.html",
  styleUrls: ["./custella-contracts-table.component.scss"],
  standalone: false,
})
export class CustellaContractsTableComponent
  extends SubscriptionUtil
  implements OnInit, OnChanges, OnDestroy
{
  @Output() itemSelector = new EventEmitter();

  // table
  public gridOptions!: any;
  public defaultColDef!: ColDef;
  public columnDefs: any[] = [];
  private gridParams: any;
  private gridApi: any;
  public overlayNoRowsTemplate: any;

  public totalRecords: any;
  public noData: boolean | null = null;

  @Input() searchValue = "";
  @Input() customerId: any;
  @Input() translate: any;

  loadTable: any = 0;

  sortBy: any;
  sortOrder: any;

  // ✅ Define header ID map as class property - single source of truth
  private readonly headerIdMap: { [colId: string]: string } = {
    'contractNumber': 'Tbl_HeadContractNo',
    'customer.name':  'Tbl_HeadContractCust',
  };

  constructor(
    private assetService: AssetService,
    private errorUtil: ErrorUtil,
    private dynamicFormService: DynamicFormService,
    private dynamicTableColumnsService: DynamicTableColumnsService,
    private languageTranslateService: LanguageTranslateService
  ) {
    super();
    this.preInit();
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes['searchValue'] &&
      changes['searchValue'].previousValue !== changes['searchValue'].currentValue &&
      changes['searchValue'].currentValue.length > 2
    ) {
      this.refresh();
    }

    if (this.searchValue == "") this.refresh();

    if (
      changes['customerId'] &&
      changes['customerId'].currentValue !== changes['customerId'].previousValue
    ) {
      this.refresh();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /******************************************** Pre Init ******************************************/
  private preInit() {
    this.gridOptions = {
      cacheBlockSize: 25,
      rowModelType: "infinite",
      sortingOrder: ["desc", "asc"],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      // ✅ Set header IDs after first data render
      onFirstDataRendered: () => {
        Global.setHeaderIds(this.headerIdMap);
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      enableRowGroup: false,
      enablePivot: false,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: false,
      tooltipComponent: TableHeaderToolTipComponent,
    };
  }

  /******************************************** Init ******************************************/
  private init() {
    this.overlayNoRowsTemplate =
      "<div class='not-found'><span>" +
      this.translate?.general.messages.noRecordsFound +
      "</span> </div>";

    this.dynamicFormService.getTableColumn("contract").subscribe((resp: any) => {
      if (resp && resp.tableColumnSetups) {
        this.sortBy = resp.sortBy;
        this.sortOrder = resp.sortOrder;
        this.configureColumns(resp.tableColumnSetups);

        // ✅ AG Grid v35: use setGridOption instead of setColumnDefs
        if (this.gridApi) {
          this.gridApi.setGridOption("columnDefs", this.columnDefs);
          this.gridApi.refreshHeader();
          setTimeout(() => {
            this.gridApi.sizeColumnsToFit();
            // ✅ Re-apply IDs after columns load
            Global.setHeaderIds(this.headerIdMap);
          }, 0);
        }
      }

      this.loadTable = 1;
      this.onGridReady(this.gridParams);
    });
  }

  /******************************************** Configure Columns ******************************************/
  private configureColumns(tableColumns: any[]) {
    // ✅ Reset to avoid duplication on re-init
    this.columnDefs = [];

    tableColumns.forEach((tc) => {
      const column: any = {
        field: tc.field,
        headerName: tc.headerName,
        hide: tc.hide,
        cellClass: tc.cellClass,
        width: 200,
        headerTooltip: "#",
        cellRenderer: <any>null,
        headerComponentParams: <any>{},
      };

      if (tc.translatePath) {
        column.headerName = this.languageTranslateService.getNestedValue(
          this.translate,
          tc.translatePath
        );
      }

      tc.headerTooltip = "#";

      if (tc.field === "contractNumber") {
        column.cellRenderer = function (params: any) {
          if (params.data) {
            return Global.setCellRenderer(params, params.data.rowId, params.data.contractNumber);
          }
        };
      }

      if (tc.field === "customer.name") {
        column.cellRenderer = function (params: any) {
          if (params.data && params.data.customer) {
            return Global.setCellRenderer(params, params.data.rowId2, params.data.customer.name);
          }
        };
      }

      if (tc.field === "endDateTime") {
        column.cellRenderer = (params: any) => {
          if (params.data) {
            if (
              params.data.endDateTime !== null &&
              params.data.endDateTime !== undefined &&
              params.data.endDateTime !== ""
            ) {
              return (
                moment(params.data.endDateTime).format(
                  localStorage.getItem("date_format")?.toUpperCase(),
                ) +
                ", " +
                moment(params.data.endDateTime).format("hh:mm A")
              );
            } else {
              return "-";
            }
          }
        };
      }

      if (tc.field === "startDateTime") {
        column.cellRenderer = (params: any) => {
          if (params.data) {
            if (
              params.data.startDateTime !== null &&
              params.data.startDateTime !== undefined &&
              params.data.startDateTime !== ""
            ) {
              return (
                moment(params.data.startDateTime).format(
                  localStorage.getItem("date_format")?.toUpperCase(),
                ) +
                ", " +
                moment(params.data.startDateTime).format("hh:mm A")
              );
            } else {
              return "-";
            }
          }
        };
      }

      if (tc.field === "status") {
        column.cellRenderer = (params: any) => {
          if (params.data) {
            if (params.data.status == "ON_RENT") {
              return (
                '<span class="p-tag p-component on-rent"><span class="p-tag-value text-dark">' +
                this.translate.general.status.onRent.singular +
                '</span></span>'
              );
            } else if (params.data.status == "EXPIRED") {
              return (
                '<span class="p-tag p-component expire"><span class="p-tag-value text-dark">' +
                this.translate.general.status.expired.singular +
                '</span></span>'
              );
            } else if (params.data.status == "NEW") {
              return (
                '<span class="p-tag p-component new"><span class="p-tag-value text-dark">' +
                this.translate.general.status.new.singular +
                '</span></span>'
              );
            } else if (params.data.status == "TERMINATED") {
              return (
                '<span class="p-tag p-component terminate"><span class="p-tag-value text-dark">' +
                this.translate.general.status.terminated.singular +
                '</span></span>'
              );
            }
          }
        };
      }

      this.columnDefs.push(column);
    });
  }

  /******************************************** On Table Ready ******************************************/
  public onGridReady(params: any) {
    if (params) {
      this.gridParams = params;
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();

      // ✅ AG Grid v35: use IDatasource with IGetRowsParams
      const dataSource: IDatasource = {
        getRows: (dsParams: IGetRowsParams) => {
          if (this.loadTable == 1) {
            this.getServerSideData(dsParams as any);
          }
        },
      };

      this.gridApi.sizeColumnsToFit();
      // ✅ AG Grid v35: use setGridOption instead of setDatasource
      this.gridApi.setGridOption("datasource", dataSource);
    }
  }

  /******************************************** Get Data ******************************************/
  private getServerSideData(agGridGetRowsParams: any) {
    this.gridApi.hideOverlay();
    const serverRequest = this.getRequestParams(agGridGetRowsParams);
    var count: any = serverRequest.startRow;

    const request$ = this.customerId
      ? this.assetService.getContractByCustomer(
          serverRequest?.startRow + 1,
          25,
          this.searchValue,
          null,
          serverRequest,
          this.customerId
        )
      : this.assetService.getContractGrid(
          serverRequest.startRow,
          25,
          this.searchValue,
          null,
          serverRequest
        );

    request$.subscribe(
      (resp: any) => {
        if (resp && resp.body) {
          resp.body.data.forEach((element: any) => {
            count++;
            element.rowId = 'Tbl_RecContractNo' + count;
            element.rowId2 = 'Tbl_RecContractCust' + count;
          });

          this.setGridData(agGridGetRowsParams, resp.body.data, resp.body.total, resp.body.status);
        }
      },
      (err) => {
        this.errorUtil.setErrorMessage(err);
      }
    );
  }

  /******************************************** Get Params ******************************************/
  private getRequestParams(agGridRequest: any): ServerRequest {
    if (agGridRequest.sortModel.length <= 0) {
      const parameters = { colId: this.sortBy, sort: this.sortOrder };
      agGridRequest.sortModel.push(parameters);
    }

    return {
      startRow: agGridRequest.startRow,
      pageSize: 25,
      filterModel: null,
      sortModel: agGridRequest.sortModel,
    };
  }

  /******************************************** Set Data ******************************************/
  private setGridData(
    agGridGetRowsParams: any,
    resultItems: any[],
    totalCount: number,
    status: string
  ) {
    this.gridApi.hideOverlay();
    this.totalRecords = totalCount;

    if (status === "NO_DATA") {
      this.noData = true;
      this.gridApi.showNoRowsOverlay();
    } else {
      this.noData = false;

      if (resultItems.length === 0) {
        this.gridApi.showNoRowsOverlay();
      } else {
        // ✅ AG Grid v35: successCallback for infinite row model
        agGridGetRowsParams.successCallback(resultItems, totalCount);
      }
    }
  }

  /******************************************** Refresh Table ******************************************/
  public refresh() {
    this.onGridReady(this.gridParams);
  }

  /******************************************** View Cell ******************************************/
  public viewCell($event: any) {
    const field = $event.colDef.field;

    if (field === "contractNumber") {
      this.itemSelector.emit($event.data);
    }
  }

  /******************************************** Table Context Menu ******************************************/
  public getContextMenuItems = (params: any) => {
    return this.dynamicTableColumnsService.rightClick(params);
  };

  /******************************************** Add All the Subscriptions ******************************************/
  push(obs: any) {
    super.push(obs);
  }
}
